function downloadCanvas(canvas, filename) {
  const link = document.createElement('a');
  link.download = filename;
  link.href = canvas.toDataURL('image/jpeg', 0.96);
  link.click();
}

function makeCanvas(width, height) {
  const scale = 2;
  const canvas = document.createElement('canvas');
  canvas.width = width * scale;
  canvas.height = height * scale;
  const context = canvas.getContext('2d');
  context.scale(scale, scale);
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, width, height);
  return { canvas, context };
}

const FONT = "'League Spartan', Arial, sans-serif";
const EXPORT_COLORS = {
  page: '#f4f7fa',
  surface: '#ffffff',
  surfaceMuted: '#edf2f7',
  person: '#d7dbe0',
  border: '#87929d',
  borderStrong: '#46515d',
  borderSoft: '#c2cbd4',
  text: '#111820',
  textDim: '#25313d',
  textMute: '#8f99a4',
  accent: '#2f67df',
};

function cell(context, x, y, width, height, fill, stroke = EXPORT_COLORS.borderSoft, lineWidth = 0.95) {
  context.fillStyle = fill;
  context.fillRect(x, y, width, height);
  context.strokeStyle = stroke;
  context.lineWidth = lineWidth;
  context.strokeRect(x + 0.5, y + 0.5, width - 1, height - 1);
}

function ellipsize(context, text, maxWidth) {
  const value = String(text || '');
  if (context.measureText(value).width <= maxWidth) return value;
  const ellipsis = '...';
  let low = 0;
  let high = value.length;
  while (low < high) {
    const mid = Math.ceil((low + high) / 2);
    if (context.measureText(`${value.slice(0, mid)}${ellipsis}`).width <= maxWidth) low = mid;
    else high = mid - 1;
  }
  return `${value.slice(0, low).trimEnd()}${ellipsis}`;
}

function centeredText(context, text, x, y, width, height, font, color = EXPORT_COLORS.text) {
  context.fillStyle = color;
  context.font = font;
  context.textAlign = 'center';
  context.textBaseline = 'middle';
  context.fillText(ellipsize(context, text, width - 14), x + width / 2, y + height / 2);
}

function shiftFill(shift) {
  if (shift.status === 'LIBRE') return '#b7e3cf';
  if (shift.status === 'AUSENTE') return '#f5d4d4';
  if (['LICENCIA', 'LIC. MEDICA', 'SUSPENDIDO'].includes(shift.status)) return '#f2e4bc';
  if (shift.status === 'NORMAL') return '#f5f9fd';
  return '#f7fafc';
}

function drawNormalShift(context, shift, x, y, width, height) {
  const center = x + width / 2;
  const hourBandHeight = Math.max(18, Math.round(height * 0.38));
  const location = String(shift.locationShort || shift.location || '').trim();
  context.strokeStyle = EXPORT_COLORS.border;
  context.lineWidth = 0.9;
  context.beginPath();
  context.moveTo(x + 0.5, y + hourBandHeight + 0.5);
  context.lineTo(x + width - 0.5, y + hourBandHeight + 0.5);
  context.stroke();

  const schedule = String(shift.hours || '09:00 - 17:00').replace(/\s+/g, ' ');
  const metaY = y + hourBandHeight / 2;
  const maxMetaWidth = width - 16;
  const metaGap = 6;

  context.textBaseline = 'middle';
  context.font = `800 13px ${FONT}`;
  const scheduleWidth = context.measureText(schedule).width;
  context.font = `650 13px ${FONT}`;
  const dividerWidth = location ? context.measureText('|').width : 0;
  context.font = `700 12px ${FONT}`;
  const availableLocationWidth = Math.max(0, maxMetaWidth - scheduleWidth - (location ? dividerWidth + metaGap * 2 : 0));
  const locationText = location ? ellipsize(context, `📍 ${location}`, availableLocationWidth) : '';
  const locationWidth = locationText ? context.measureText(locationText).width : 0;
  const metaWidth = scheduleWidth + (locationText ? dividerWidth + locationWidth + metaGap * 2 : 0);
  let cursor = center - metaWidth / 2;

  context.fillStyle = EXPORT_COLORS.text;
  context.font = `800 13px ${FONT}`;
  context.textAlign = 'left';
  context.fillText(schedule, cursor, metaY);
  cursor += scheduleWidth;
  if (locationText) {
    cursor += metaGap;
    context.font = `650 13px ${FONT}`;
    context.fillStyle = EXPORT_COLORS.borderStrong;
    context.fillText('|', cursor, metaY);
    cursor += dividerWidth + metaGap;
    context.font = `700 12px ${FONT}`;
    context.fillStyle = EXPORT_COLORS.textDim;
    context.fillText(locationText, cursor, metaY);
  }

  context.textAlign = 'center';
  context.fillStyle = EXPORT_COLORS.text;
  context.font = `650 16px ${FONT}`;
  context.fillText(
    ellipsize(context, String(shift.label || 'LOGISTICA').toUpperCase(), width - 12),
    center,
    y + hourBandHeight + (height - hourBandHeight) / 2,
  );
}

function drawStatusShift(context, shift, x, y, width, height) {
  const isEmpty = shift.status === 'VACIO';
  centeredText(
    context,
    shift.label || shift.status || 'VACIO',
    x,
    y,
    width,
    height,
    isEmpty ? `italic 520 16px ${FONT}` : `650 16px ${FONT}`,
    isEmpty ? EXPORT_COLORS.textMute : EXPORT_COLORS.text,
  );
}

export function exportPlanRangeJpg({ days, people, rangeFrom, rangeTo, shiftFor }) {
  const selectedDays = days.slice(rangeFrom, rangeTo + 1);
  const personWidth = 165;
  const dayWidth = 169;
  const titleHeight = 0;
  const headerHeight = 58;
  const segmentHeight = 64;
  const rowHeights = people.map((person) => segmentHeight * Math.max(1,
    ...selectedDays.map((day) => shiftFor(person, day).segments?.length || 1)));
  const groupGap = 7;
  const width = personWidth + selectedDays.length * dayWidth;
  const breaks = people.filter((person) => person.breakBefore).length;
  const height = titleHeight + headerHeight + rowHeights.reduce((sum, value) => sum + value, 0) + breaks * groupGap;
  const { canvas, context } = makeCanvas(width, height);

  context.fillStyle = EXPORT_COLORS.page;
  context.fillRect(0, 0, width, height);

  cell(context, 0, titleHeight, personWidth, headerHeight, EXPORT_COLORS.surfaceMuted, EXPORT_COLORS.borderStrong, 1.15);
  centeredText(context, String(selectedDays[0]?.month || '').toUpperCase(), 0, titleHeight, personWidth, headerHeight, `900 23px ${FONT}`, EXPORT_COLORS.text);

  selectedDays.forEach((day, index) => {
    const x = personWidth + index * dayWidth;
    cell(context, x, titleHeight, dayWidth, headerHeight, EXPORT_COLORS.surfaceMuted, EXPORT_COLORS.borderStrong, 1.15);
    context.fillStyle = EXPORT_COLORS.text;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.font = `900 16px ${FONT}`;
    context.fillText(ellipsize(context, String(day.long || '').toUpperCase(), dayWidth - 12), x + dayWidth / 2, titleHeight + 18);
    context.font = `900 24px ${FONT}`;
    context.fillText(ellipsize(context, String(day.day || ''), dayWidth - 12), x + dayWidth / 2, titleHeight + 41);
  });

  let y = titleHeight + headerHeight;
  people.forEach((person, personIndex) => {
    const rowHeight = rowHeights[personIndex];
    if (person.breakBefore) {
      context.fillStyle = '#d7e1ec';
      context.fillRect(0, y, width, groupGap);
      y += groupGap;
    }
    cell(context, 0, y, personWidth, rowHeight, EXPORT_COLORS.person, EXPORT_COLORS.border);
    context.fillStyle = EXPORT_COLORS.text;
    context.font = `620 18px ${FONT}`;
    context.textAlign = 'center';
    context.textBaseline = 'middle';
    context.fillText(ellipsize(context, String(person.name || '').toUpperCase(), personWidth - 10), personWidth / 2, y + rowHeight / 2);
    selectedDays.forEach((day, dayIndex) => {
      const shift = shiftFor(person, day);
      const x = personWidth + dayIndex * dayWidth;
      cell(context, x, y, dayWidth, rowHeight, shiftFill(shift));
      if (shift.status === 'NORMAL') {
        const segments = shift.segments?.length ? shift.segments : [shift];
        const top = y + (rowHeight - segments.length * segmentHeight) / 2;
        segments.forEach((segment, index) => {
          const segmentY = top + index * segmentHeight;
          if (index) {
            context.fillStyle = EXPORT_COLORS.borderStrong;
            context.fillRect(x, segmentY, dayWidth, 1.5);
          }
          drawNormalShift(context, segment, x, segmentY, dayWidth, segmentHeight);
        });
      }
      else drawStatusShift(context, shift, x, y, dayWidth, rowHeight);
    });
    y += rowHeight;
  });

  const from = selectedDays[0]?.iso || 'desde';
  const to = selectedDays[selectedDays.length - 1]?.iso || 'hasta';
  downloadCanvas(canvas, `plan-semanal-${from}-${to}.jpg`);
}

function wrapLines(context, text, maxWidth) {
  const words = String(text || '').split(/\s+/).filter(Boolean);
  const lines = [];
  let line = '';
  words.forEach((word) => {
    const next = line ? `${line} ${word}` : word;
    if (!line || context.measureText(next).width <= maxWidth) line = next;
    else {
      lines.push(line);
      line = word;
    }
  });
  if (line) lines.push(line);
  return lines;
}

export function exportPlanDayJpg({ day, rows }) {
  const width = 760;
  const padding = 32;
  const headerHeight = 116;
  const cardGap = 12;
  const exportRows = rows.length ? rows.flatMap((row) => (
    row.shift.segments?.length ? row.shift.segments.map((segment) => ({ ...row, shift: segment })) : [row]
  )) : [{ personName: 'Sin personas planificadas', shift: { label: 'VACIO', hours: '' } }];
  const rowHeight = 78;
  const height = padding * 2 + headerHeight + exportRows.length * rowHeight + Math.max(0, exportRows.length - 1) * cardGap;
  const { canvas, context } = makeCanvas(width, height);

  context.fillStyle = EXPORT_COLORS.page;
  context.fillRect(0, 0, width, height);
  context.fillStyle = EXPORT_COLORS.textDim;
  context.font = `800 13px ${FONT}`;
  context.textAlign = 'left';
  context.textBaseline = 'alphabetic';
  context.fillText('RESUMEN DEL DIA', padding, padding + 22);
  context.fillStyle = EXPORT_COLORS.text;
  context.font = `800 30px ${FONT}`;
  context.fillText(`${day.long} ${day.day}`, padding, padding + 62);
  context.fillStyle = EXPORT_COLORS.textDim;
  context.font = `700 15px ${FONT}`;
  context.fillText(`${rows.length} registros planificados`, padding, padding + 91);

  let y = padding + headerHeight;
  exportRows.forEach(({ personName, shift }) => {
    cell(context, padding, y, width - padding * 2, rowHeight, EXPORT_COLORS.surface, EXPORT_COLORS.border);
    context.fillStyle = EXPORT_COLORS.text;
    context.font = `800 18px ${FONT}`;
    context.textAlign = 'left';
    context.textBaseline = 'alphabetic';
    const lines = wrapLines(context, personName, width - padding * 2 - 210);
    context.fillText(lines[0] || '', padding + 18, y + 31);
    context.fillStyle = EXPORT_COLORS.textDim;
    context.font = `700 14px ${FONT}`;
    context.fillText(shift.label || shift.status || 'VACIO', padding + 18, y + 56);
    context.fillStyle = EXPORT_COLORS.text;
    context.font = `800 17px ${FONT}`;
    context.textAlign = 'right';
    context.fillText(shift.hours || shift.status || '', width - padding - 18, y + 42);
    y += rowHeight + cardGap;
  });

  downloadCanvas(canvas, `resumen-dia-${day.iso}.jpg`);
}
